/**
 * Lightweight, Sentry-style client error reporter. Posts caught frontend
 * errors to `/api/client-errors` (see `ClientErrorController`) on a
 * best-effort basis — failures of the report itself are swallowed so we never
 * loop or surface noise to the user.
 *
 * Three sources funnel through here:
 *   - `render` — React render crashes caught by `AppErrorBoundary`.
 *   - `xhr`    — failed react-query queries/mutations (`reportXhrError`).
 *   - `window` — uncaught synchronous errors (`registerGlobalErrorReporting`).
 *
 * XHR reporting is intentionally limited to server (5xx) and network/timeout
 * failures. Expected 4xx outcomes (401 session expiry, 403 permissions, 422
 * validation) are normal client-side flow and would only pollute the log.
 */

import axios from 'axios';

const ENDPOINT = '/api/client-errors';

export type ClientErrorSource = 'render' | 'xhr' | 'window';

export interface ClientErrorDetails {
    message: string;
    source: ClientErrorSource;
    component_stack?: string | null;
    stack?: string | null;
    url?: string;
}

export function reportClientError(details: ClientErrorDetails): void {
    fetch(ENDPOINT, {
        body: JSON.stringify({
            component_stack: details.component_stack ?? null,
            message: details.message,
            source: details.source,
            stack: details.stack ?? null,
            url: details.url ?? window.location.href,
            user_agent: navigator.userAgent,
        }),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        method: 'POST',
    }).catch(() => { /* best-effort: never let reporting throw */ });
}

/**
 * Whether a failed request is worth reporting. Network/timeout failures (no
 * response) and 5xx responses are real problems; every 4xx is expected flow.
 * Non-axios errors thrown from a `queryFn` (e.g. a bug in a `.then()` mapper)
 * are genuine bugs and always reported.
 */
function shouldReportXhr(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
        return true;
    }

    const status = error.response?.status;

    return status === undefined || status >= 500;
}

function describeXhr(error: unknown): { message: string; stack: string | null } {
    if (axios.isAxiosError(error)) {
        const method = error.config?.method?.toUpperCase() ?? 'GET';
        const url = error.config?.url ?? 'unknown';
        const outcome = error.response?.status ?? error.code ?? 'network error';

        return {
            message: `XHR ${ method } ${ url } → ${ outcome }`,
            stack: error.stack ?? null,
        };
    }

    const fallback = error instanceof Error ? error : null;

    return {
        message: fallback?.message ?? String(error),
        stack: fallback?.stack ?? null,
    };
}

/**
 * react-query `QueryCache`/`MutationCache` `onError` handler. Wiring this once
 * on the `QueryClient` covers every hook without touching their `queryFn`s.
 */
export function reportXhrError(error: unknown): void {
    if (!shouldReportXhr(error)) {
        return;
    }

    const { message, stack } = describeXhr(error);

    reportClientError({ message, source: 'xhr', stack });
}

/**
 * Reports uncaught synchronous errors that escape React (event handlers,
 * timers, etc.). Resource-load errors (`<img>`/`<script>`) surface with no
 * `error` object and are skipped.
 */
export function registerGlobalErrorReporting(): void {
    window.addEventListener('error', (event: ErrorEvent) => {
        if (!event.error && !event.message) {
            return;
        }

        reportClientError({
            message: event.message || 'Uncaught error',
            source: 'window',
            stack: event.error?.stack ?? null,
        });
    });
}
